// apps/web/src/lib/dev-errors/DevErrorContext.tsx
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export type DevErrorSource = 'react' | 'promise' | 'uncaught' | 'trpc' | 'explicit';

export interface DevErrorRecord {
  id: string;
  timestamp: Date;
  source: DevErrorSource;
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
}

export interface DevErrorContextValue {
  records: DevErrorRecord[];
  clearAll: () => void;
  dismiss: (id: string) => void;
}

const DevErrorContext = createContext<DevErrorContextValue | null>(null);

// Module-level handler pointer — set by DevErrorProvider on mount, null on unmount.
let _pushRecord: ((record: DevErrorRecord) => void) | null = null;

export const _setDevErrorHandler = (
  handler: ((record: DevErrorRecord) => void) | null,
): void => {
  _pushRecord = handler;
};

export const devError = (
  error: unknown,
  context?: Record<string, unknown>,
  source: DevErrorSource = 'explicit',
): void => {
  if (!import.meta.env.DEV) return;
  _pushRecord?.({
    id: crypto.randomUUID(),
    timestamp: new Date(),
    source,
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    context,
  });
};

export function DevErrorProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<DevErrorRecord[]>([]);

  const pushRecord = useCallback((record: DevErrorRecord) => {
    setRecords((prev) => [record, ...prev].slice(0, 50));
  }, []);

  const clearAll = useCallback(() => setRecords([]), []);
  const dismiss = useCallback(
    (id: string) => setRecords((prev) => prev.filter((r) => r.id !== id)),
    [],
  );

  useEffect(() => {
    _setDevErrorHandler(pushRecord);
    return () => _setDevErrorHandler(null);
  }, [pushRecord]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleWindowError = (event: ErrorEvent) => {
      pushRecord({
        id: crypto.randomUUID(),
        timestamp: new Date(),
        source: 'uncaught',
        message: event.message,
        stack: event.error?.stack,
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const err = event.reason;
      pushRecord({
        id: crypto.randomUUID(),
        timestamp: new Date(),
        source: 'promise',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
    };

    const originalConsoleError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      originalConsoleError(...args);
      const message = args
        .map((a) =>
          a instanceof Error ? a.message : typeof a === 'string' ? a : JSON.stringify(a),
        )
        .join(' ');
      const errArg = args.find((a): a is Error => a instanceof Error);
      pushRecord({
        id: crypto.randomUUID(),
        timestamp: new Date(),
        source: 'explicit',
        message,
        stack: errArg?.stack,
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleRejection);
      console.error = originalConsoleError;
    };
  }, [pushRecord]);

  return (
    <DevErrorContext.Provider value={{ records, clearAll, dismiss }}>
      {children}
    </DevErrorContext.Provider>
  );
}

export function useDevError(): DevErrorContextValue {
  const ctx = useContext(DevErrorContext);
  if (!ctx) throw new Error('useDevError must be used within DevErrorProvider');
  return ctx;
}
